import React, { useState, useEffect } from 'react';
import { X, ChevronRight, ChevronLeft, Copy, Check, Plus, Minus } from 'lucide-react';
import axiosInstance from '../../../shared/services/api/axiosInstance';
import { useTeam } from '../../../shared/context/teamContext';
import WebsiteSelector from '../../../shared/components/analytics/WebsiteSelector';

const CreateCampaignForm = ({ onClose, onCampaignCreated }) => {
  const { selectedTeam } = useTeam();
  const [currentStep, setCurrentStep] = useState(1);
  const [showOptionalFields, setShowOptionalFields] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(null);
  const [generatedCampaign, setGeneratedCampaign] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    domain: '',
    path: '/',
    source: '',
    medium: '',
    campaign: '',
    term: '',
    content: ''
  });
  const [selectedSiteId, setSelectedSiteId] = useState('');

  const styles = {
    primaryBg: "#ffffff",
    secondaryBg: "#DDE2EE",
    textPrimary: "#0f172a",
    textSecondary: "#666666",
    accentColor: "#4b57aa",
    borderColor: "#DDE2EE",
    buttonPrimary: "#4b57aa",
    buttonPrimaryHover: "#3d4799"
  };

  const MODE_ACTIVE_COLOR = '#4b57aa';

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError(null);
  };

  const handleDomainChange = (domain) => {
    const normalized = (domain || '').replace(/\/+$/, '');
    setFormData(prev => ({ ...prev, domain: normalized }));
  };

  const handleNext = () => {
    if (currentStep === 1) {
      if (!formData.name.trim()) {
        setError('Please enter a campaign name');
        return;
      }
    } else if (currentStep === 2) {
      if (!formData.domain) {
        setError('Please select a website');
        return;
      }
    } else if (currentStep === 3) {
      if (!formData.source.trim() || !formData.medium.trim()) {
        setError('Please fill in Source and Medium fields');
        return;
      }
      handleCreateCampaign();
      return;
    }
    setError(null);
    setCurrentStep(prev => prev + 1);
  };

  const handleBack = () => {
    setError(null);
    setCurrentStep(prev => prev - 1);
  };

  const handleCreateCampaign = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await axiosInstance.post('/campaign-links/create', {
        ...formData,
        domain: formData.domain,
        siteId: selectedSiteId || null,
        generateShort: true,
        teamId: selectedTeam?._id || null
      });

      if (response.data.success) {
        setGeneratedCampaign(response.data.campaign);
        setCurrentStep(4);
        if (typeof onCampaignCreated === 'function') {
          onCampaignCreated(response.data.campaign);
        }
      }
    } catch (err) {
      console.error('Error creating campaign:', err);
      setError(err.response?.data?.error || 'Failed to create campaign. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async (text, type) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(type);
      setTimeout(() => setCopied(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const getProgressPercentage = () => {
    return (currentStep / 4) * 100;
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-lg font-semibold mb-2" style={{ color: styles.textPrimary, fontFamily: 'Raleway, sans-serif' }}>
                What would you like to call this campaign?
              </h2>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder="Campaign Name"
                className="w-full px-4 py-3 text-sm rounded-lg focus:outline-none border border-gray-300"
                style={{
                  fontFamily: 'Raleway, sans-serif',
                  backgroundColor: styles.primaryBg,
                  color: styles.textPrimary
                }}
                autoFocus
              />
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-lg font-semibold mb-4" style={{ color: styles.textPrimary, fontFamily: 'Raleway, sans-serif' }}>
                Where should your user redirect to?
              </h2>
              <div className="flex items-center gap-0">
                <div className="flex-1">
                  <WebsiteSelector
                    selectedSiteId={selectedSiteId}
                    onSiteChange={(siteId) => {
                      setSelectedSiteId(siteId);
                    }}
                    teamId={selectedTeam?._id || null}
                    className="w-full"
                    placeholder="Select website"
                    labelText=""
                    showLabel={false}
                    onDomainChange={handleDomainChange}
                  />
                </div>
                <div className="flex-1">
                  <input
                    type="text"
                    name="path"
                    value={formData.path}
                    onChange={handleInputChange}
                    placeholder="/path"
                    className="w-full px-4 py-3 text-sm rounded-lg focus:outline-none border-l-0 border border-gray-300 rounded-l-none"
                    style={{
                      fontFamily: 'Raleway, sans-serif',
                      backgroundColor: styles.primaryBg,
                      color: styles.textPrimary
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-lg font-semibold mb-2" style={{ color: styles.textPrimary, fontFamily: 'Raleway, sans-serif' }}>
                Where is the user coming from?
              </h2>
              
              <div className="space-y-4">
                <div>
                  <input
                    type="text"
                    name="source"
                    value={formData.source}
                    onChange={handleInputChange}
                    placeholder="Source"
                    className="w-full px-4 py-3 text-sm rounded-lg focus:outline-none border border-gray-300"
                    style={{
                      fontFamily: 'Raleway, sans-serif',
                      backgroundColor: styles.primaryBg,
                      color: styles.textPrimary
                    }}
                  />
                </div>
                
                <div>
                  <input
                    type="text"
                    name="medium"
                    value={formData.medium}
                    onChange={handleInputChange}
                    placeholder="Medium"
                    className="w-full px-4 py-3 text-sm rounded-lg focus:outline-none border border-gray-300"
                    style={{
                      fontFamily: 'Raleway, sans-serif',
                      backgroundColor: styles.primaryBg,
                      color: styles.textPrimary
                    }}
                  />
                </div>

                {/* Optional UTM Fields - Collapsible */}
                <div>
                  <button
                    type="button"
                    onClick={() => setShowOptionalFields(!showOptionalFields)}
                    className="flex items-center gap-2 text-sm mb-3"
                    style={{ color: styles.textSecondary, fontFamily: 'Raleway, sans-serif' }}
                  >
                    {showOptionalFields ? <Minus size={16} /> : <Plus size={16} />}
                    <span>Other optional UTM Fields</span>
                  </button>

                  {showOptionalFields && (
                    <div className="space-y-4 pl-6">
                      <div>
                        <input
                          type="text"
                          name="campaign"
                          value={formData.campaign}
                          onChange={handleInputChange}
                          placeholder="Campaign"
                          className="w-full px-4 py-3 text-sm rounded-lg focus:outline-none border border-gray-300"
                          style={{
                            fontFamily: 'Raleway, sans-serif',
                            backgroundColor: styles.primaryBg,
                            color: styles.textPrimary
                          }}
                        />
                      </div>
                      
                      <div>
                        <input
                          type="text"
                          name="term"
                          value={formData.term}
                          onChange={handleInputChange}
                          placeholder="Term"
                          className="w-full px-4 py-3 text-sm rounded-lg focus:outline-none border border-gray-300"
                          style={{
                            fontFamily: 'Raleway, sans-serif',
                            backgroundColor: styles.primaryBg,
                            color: styles.textPrimary
                          }}
                        />
                      </div>
                      
                      <div>
                        <input
                          type="text"
                          name="content"
                          value={formData.content}
                          onChange={handleInputChange}
                          placeholder="Content"
                          className="w-full px-4 py-3 text-sm rounded-lg focus:outline-none border border-gray-300"
                          style={{
                            fontFamily: 'Raleway, sans-serif',
                            backgroundColor: styles.primaryBg,
                            color: styles.textPrimary
                          }}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-lg font-semibold mb-4" style={{ color: styles.textPrimary, fontFamily: 'Raleway, sans-serif' }}>
                Copy your links
              </h2>
              
              {generatedCampaign && (
                <div className="space-y-4">
                  {/* Short Link */}
                  {generatedCampaign.shortUrl && (
                    <div>
                      <label className="block text-xs font-medium mb-2" style={{ color: styles.textSecondary, fontFamily: 'Raleway, sans-serif' }}>
                        Short Link
                      </label>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={generatedCampaign.shortUrl}
                          readOnly
                          className="flex-1 px-4 py-3 text-sm rounded-lg border border-gray-300"
                          style={{
                            fontFamily: 'Raleway, sans-serif',
                            backgroundColor: styles.primaryBg,
                            color: styles.textPrimary
                          }}
                        />
                        <button
                          onClick={() => handleCopy(generatedCampaign.shortUrl, 'short')}
                          className="px-4 py-3 rounded-lg transition-colors border border-gray-300 bg-white hover:bg-gray-50"
                          style={{ fontFamily: 'Raleway, sans-serif' }}
                        >
                          {copied === 'short' ? (
                            <Check size={18} className="text-green-600" />
                          ) : (
                            <Copy size={18} style={{ color: styles.textSecondary }} />
                          )}
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Long Link */}
                  <div>
                    <label className="block text-xs font-medium mb-2" style={{ color: styles.textSecondary, fontFamily: 'Raleway, sans-serif' }}>
                      Long Link
                    </label>
                    <div className="flex gap-2">
                        <input
                          type="text"
                          value={generatedCampaign.fullUrl}
                          readOnly
                          className="flex-1 px-4 py-3 text-sm rounded-lg border border-gray-300"
                          style={{
                            fontFamily: 'Raleway, sans-serif',
                            backgroundColor: styles.primaryBg,
                            color: styles.textPrimary
                          }}
                        />
                      <button
                        onClick={() => handleCopy(generatedCampaign.fullUrl, 'full')}
                        className="px-4 py-3 rounded-lg transition-colors border border-gray-300 bg-white hover:bg-gray-50"
                        style={{ fontFamily: 'Raleway, sans-serif' }}
                      >
                        {copied === 'full' ? (
                          <Check size={18} className="text-green-600" />
                        ) : (
                          <Copy size={18} style={{ color: styles.textSecondary }} />
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div
        className="relative w-full max-w-2xl bg-white rounded-2xl shadow-2xl flex flex-col max-h-[90vh]"
        onClick={(e) => e.stopPropagation()}
        style={{ zIndex: 50 }}
      >
        {/* Progress Bar at top border */}
        <div className="w-full h-0.5 flex justify-center" style={{ backgroundColor: 'transparent' }}>
          <div className="h-full" style={{ width: 'calc(100% - 3rem)', backgroundColor: styles.secondaryBg }}>
            <div
              className="h-full transition-all duration-300"
              style={{
                width: `${getProgressPercentage()}%`,
                backgroundColor: MODE_ACTIVE_COLOR
              }}
            />
          </div>
        </div>

        {/* Header with Close Button */}
        <div className="px-6 py-4 border-b" style={{ borderColor: styles.borderColor }}>
          <div className="flex items-center justify-between">
            <div className="text-sm font-medium" style={{ color: styles.textPrimary, fontFamily: 'Raleway, sans-serif' }}>
              Create Campaign
            </div>
            <button
              onClick={onClose}
              className="text-[#475569] hover:text-[#475569] transition-colors p-2"
              aria-label="Close"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-6 relative" style={{ zIndex: 1 }}>
          {error && (
            <div className="mb-4 p-3 rounded-lg" style={{ backgroundColor: '#FEE2E2', color: '#DC2626', fontFamily: 'Raleway, sans-serif' }}>
              {error}
            </div>
          )}

          {renderStepContent()}
        </div>

        {/* Footer with Navigation Buttons */}
        <div className="flex items-center justify-end gap-2 px-6 py-4 border-t relative z-10" style={{ borderColor: styles.borderColor }}>
          {currentStep > 1 && (
            <button
              onClick={handleBack}
              className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-lg transition-colors border border-gray-300"
              style={{
                fontFamily: 'Raleway, sans-serif',
                backgroundColor: styles.primaryBg,
                color: styles.textSecondary
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#f9fafb';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = styles.primaryBg;
              }}
            >
              <ChevronLeft size={16} />
              Back
            </button>
          )}

          {currentStep < 4 ? (
            <button
              onClick={handleNext}
              disabled={loading}
              className={`px-3 py-1.5 text-sm rounded-lg transition-colors border border-gray-300 ${
                loading
                  ? 'bg-gray-100 text-[#475569] cursor-not-allowed'
                  : 'bg-[#4b57aa] text-white hover:bg-[#3d4799]'
              }`}
              style={{ fontFamily: 'Raleway, sans-serif' }}
            >
              {currentStep === 3 ? (
                loading ? 'Creating...' : 'Create Campaign'
              ) : (
                <>
                  Next
                  <ChevronRight size={16} className="inline ml-1" />
                </>
              )}
            </button>
          ) : (
            <button
              onClick={onClose}
              className="px-3 py-1.5 text-sm rounded-lg transition-colors border border-gray-300 bg-[#4b57aa] text-white hover:bg-[#3d4799]"
              style={{ fontFamily: 'Raleway, sans-serif' }}
            >
              Done
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CreateCampaignForm;
